import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Bookmark, ArrowRightLeft, Eye, PackageCheck, Truck, Printer, FileDown, ChevronLeft, ChevronRight, Trash2, CalendarDays, Boxes, Pencil, XCircle, Settings2, Wrench, Route } from "lucide-react";
import { toast } from "sonner";
import { api, downloadFile, formatApiError, fmtDate, fmtIDR } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel, StatusBadge } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Label } from "../components/ui/label";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const STATUSES = ["all", "Active", "Partially Converted", "Converted to Sale", "Cancelled", "Draft"];

function pageWindow(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out = [1];
  const start = Math.max(2, current - 1), end = Math.min(total - 1, current + 1);
  if (start > 2) out.push("...");
  for (let p = start; p <= end; p++) out.push(p);
  if (end < total - 1) out.push("...");
  out.push(total);
  return out;
}

function printKeep(k) {
  const rows = (k.items || []).map((it) => `<tr><td>${it.product_name || "-"}</td><td>${it.color}/${it.size}</td><td><b>${it.warehouse_name || "-"}</b></td><td style="text-align:center">${it.qty}</td><td style="text-align:right">${(it.price || 0).toLocaleString("id-ID")}</td></tr>`).join("");
  const total = (k.items || []).reduce((s, it) => s + (it.price || 0) * it.qty, 0);
  const html = `<html><head><title>Struk Keep ${k.number}</title><style>body{font-family:Arial;padding:24px;color:#3a2a1a}h2{margin:0}table{width:100%;border-collapse:collapse;margin-top:12px}td,th{border:1px solid #ccc;padding:6px;font-size:13px}th{background:#f3ece4;text-align:left}.muted{color:#777;font-size:12px}</style></head><body><h2>STRUK KEEP</h2><div class="muted">No: <b>${k.number}</b> &middot; ${new Date(k.created_at).toLocaleString("id-ID")}</div><div class="muted">Customer: ${k.customer_name || "-"}${k.group_name ? " &middot; Group: " + k.group_name : ""}</div><table><thead><tr><th>Produk</th><th>Varian</th><th>Ambil dari Gudang</th><th style="text-align:center">Qty</th><th style="text-align:right">Harga</th></tr></thead><tbody>${rows}</tbody></table><div style="text-align:right;margin-top:10px;font-weight:bold">Total: Rp ${total.toLocaleString("id-ID")}</div><p class="muted">Catatan: ambil barang sesuai kolom "Ambil dari Gudang".</p><script>window.onload=function(){window.print()}</script></body></html>`;
  const w = window.open("", "_blank");
  if (w) { w.document.write(html); w.document.close(); }
}

export default function Keep() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], total: 0, pages: 0, page: 1 });
  const [statusF, setStatusF] = useState("Active");
  const [source, setSource] = useState("all");
  const [bucket, setBucket] = useState("all");
  const [qInput, setQInput] = useState(""); const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sum, setSum] = useState({ all: {}, online: {}, reseller: {}, period: {} });
  const [convert, setConvert] = useState(null);
  const [detail, setDetail] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [cfgOpen, setCfgOpen] = useState(false);
  const [traceData, setTraceData] = useState(null);
  const [tracing, setTracing] = useState(false);
  const [orphanOpen, setOrphanOpen] = useState(false);
  const [orphans, setOrphans] = useState([]);
  const [orphanBusy, setOrphanBusy] = useState(false);
  const [reconOpen, setReconOpen] = useState(false);
  const [recon, setRecon] = useState({ items: [], orphan_total: 0, missing_total: 0, total_rows: 0 });
  const [reconBusy, setReconBusy] = useState(false);
  const write = can("warehouse_admin", "sales");
  const CANCELLABLE = ["Active", "Partially Converted", "Draft"];

  const load = () => api.get("/keep", { params: { status: statusF === "all" ? undefined : statusF, source: source === "all" ? undefined : source, bucket: bucket === "all" ? undefined : bucket, q: q || undefined, page, limit } }).then((r) => { setData(r.data); setSelected(new Set()); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusF, source, bucket, q, page, limit]);
  useEffect(() => { api.get("/keep/summary").then((r) => setSum(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const t = setTimeout(() => { setQ(qInput); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [qInput]);
  const chg = (fn) => (v) => { fn(v); setPage(1); };

  const act = async (id, action, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try { await api.post(`/keep/${id}/${action}`); toast.success("Berhasil"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const cleanup = async () => {
    if (!window.confirm("Bersihkan semua Keep yang sudah 'Converted to Sale' & 'Cancelled'? Keep aktif tidak terpengaruh dan stok tidak berubah.")) return;
    try { const r = await api.post("/keep/cleanup"); toast.success(`${r.data.deleted} keep dibersihkan`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const repairStatus = async () => {    try {
      const dry = (await api.post("/keep/status-repair", { apply: false })).data;
      if (!dry.total) { toast.info("Semua status keep sudah benar — tidak ada yang perlu dirapikan."); return; }
      const bs = Object.entries(dry.by_status || {}).map(([s, n]) => `${n} → ${s}`).join(", ");
      if (!window.confirm(`${dry.total} keep statusnya tidak sesuai sisa qty dan akan dirapikan (${bs}).\n\nMisalnya keep "Partially Converted" yang sisanya sudah 0 akan jadi "Converted to Sale" dan keluar dari daftar aktif. Lanjutkan?`)) return;
      const r = (await api.post("/keep/status-repair", { apply: true })).data;
      toast.success(`${r.total} keep dirapikan statusnya`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const loadOrphans = async () => {
    setOrphanOpen(true); setOrphanBusy(true);
    try { const r = (await api.get("/keep/orphans")).data; setOrphans(r.items || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setOrphanBusy(false); }
  };
  const reconstructOrphans = async (ids) => {
    const n = ids ? ids.length : orphans.length;
    if (!n) { toast.info("Tidak ada keep orphan."); return; }
    if (!window.confirm(`Jadikan ${n} keep orphan menjadi Sales? Pakai ini HANYA untuk order yang SUDAH dikirim. Sistem membuat Sales dari qty terkonversi (stok & kuota tidak berubah).`)) return;
    setOrphanBusy(true);
    try {
      const r = (await api.post("/keep/orphans/reconstruct", ids ? { ids } : {})).data;
      toast.success(`${r.reconstructed} keep jadi Sales${r.failed ? `, ${r.failed} gagal` : ""}`);
      const rr = (await api.get("/keep/orphans")).data; setOrphans(rr.items || []);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setOrphanBusy(false); }
  };
  const rollbackOrphans = async (ids) => {
    const n = ids ? ids.length : orphans.length;
    if (!n) { toast.info("Tidak ada keep orphan."); return; }
    if (!window.confirm(`Kembalikan ${n} keep ke status Aktif? Konversi dibatalkan & kuota PO dikembalikan — pakai ini untuk order yang BELUM dikirim (masih "Perlu Proses" di Desty).`)) return;
    setOrphanBusy(true);
    try {
      const r = (await api.post("/keep/orphans/rollback", ids ? { ids } : {})).data;
      toast.success(`${r.restored} keep dikembalikan ke Aktif`);
      const rr = (await api.get("/keep/orphans")).data; setOrphans(rr.items || []);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setOrphanBusy(false); }
  };

  const loadRecon = async () => {
    setReconOpen(true); setReconBusy(true); setRecon({ items: [], orphan_total: 0, missing_total: 0, total_rows: 0 });
    try { const r = (await api.get("/keep/reconcile/preview")).data; setRecon(r); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setReconBusy(false); }
  };
  const applyRecon = async (keys) => {
    const orphanRows = recon.items.filter((x) => x.orphan > 0);
    const n = keys ? keys.length : orphanRows.length;
    if (!n) { toast.info("Tidak ada reservasi keep yatim untuk diperbaiki."); return; }
    const pcs = keys ? null : recon.orphan_total;
    if (!window.confirm(`Kembalikan reservasi keep yatim ke stok tersedia?${pcs ? ` (${pcs} pcs, ${n} baris)` : ` (${n} baris terpilih)`}\n\nStok yang "nyangkut" di bucket keep (tanpa dokumen keep aktif) akan dikembalikan ke stok jual.`)) return;
    setReconBusy(true);
    try {
      const r = (await api.post("/keep/reconcile/apply", keys ? { keys } : {})).data;
      toast.success(`${r.released} pcs dikembalikan ke stok tersedia (${r.fixed_rows} baris)`);
      const rr = (await api.get("/keep/reconcile/preview")).data; setRecon(rr);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setReconBusy(false); }
  };

  const doTrace = async () => {
    const term = (qInput || q || "").trim();
    if (!term) { toast.error("Ketik No. Keep atau Order ID di kotak pencarian dulu"); return; }
    setTracing(true);
    try {
      const r = (await api.get("/keep/trace", { params: { q: term } })).data;
      setTraceData(r);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTracing(false); }
  };

  const del = async (k) => {
    const msg = k.status === "Cancelled"
      ? `Hapus permanen Keep ${k.number}?`
      : `Hapus Keep ${k.number}? Stok yang masih ditahan dikembalikan ke available, lalu data dihapus permanen.`;
    if (!window.confirm(msg)) return;
    try { await api.delete(`/keep/${k.id}`); toast.success("Keep dihapus"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const pageCancellableIds = data.items.filter((k) => CANCELLABLE.includes(k.status)).map((k) => k.id);
  const allPageSelected = pageCancellableIds.length > 0 && pageCancellableIds.every((id) => selected.has(id));
  const toggleOne = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const togglePage = () => setSelected((prev) => {
    const n = new Set(prev);
    if (allPageSelected) pageCancellableIds.forEach((id) => n.delete(id));
    else pageCancellableIds.forEach((id) => n.add(id));
    return n;
  });

  const bulkCancelSelected = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Batalkan ${ids.length} Keep terpilih? Stok akan dikembalikan ke available. Data tetap tersimpan dengan status Cancelled.`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/keep/bulk-cancel", { ids });
      toast.success(`${r.data.cancelled} keep dibatalkan${r.data.skipped ? `, ${r.data.skipped} dilewati` : ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBulkBusy(false); }
  };

  const bulkCancelAllFilter = async () => {
    if (!window.confirm(`Batalkan SEMUA Keep aktif sesuai filter saat ini (${data.total} baris total, hanya yang masih menahan stok yang diproses)? Stok dikembalikan ke available.`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/keep/bulk-cancel", { select_all: true, status: statusF === "all" ? undefined : statusF, source: source === "all" ? undefined : source, bucket: bucket === "all" ? undefined : bucket, q: q || undefined });
      toast.success(`${r.data.cancelled} keep dibatalkan${r.data.skipped ? `, ${r.data.skipped} dilewati` : ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBulkBusy(false); }
  };

  const flatRows = [];
  data.items.forEach((k) => {
    const items = (k.items || []).filter((it) => ((it.qty_remaining != null ? it.qty_remaining : it.qty) || 0) > 0);
    const list = items.length ? items : [null];
    list.forEach((it, ii) => flatRows.push({ k, it, first: ii === 0 }));
  });

  return (
    <div data-testid="keep-page">
      <PageHeader title="Keep" subtitle="Menahan stok untuk customer — Keep langsung aktif (AVAILABLE → KEEP, stok fisik tidak berkurang)">
        <Button variant="outline" onClick={() => downloadFile("/keep/export", { status: statusF === "all" ? undefined : statusF, source: source === "all" ? undefined : source, bucket: bucket === "all" ? undefined : bucket, q: q || undefined }, "daftar-keep.xlsx").catch((e) => toast.error(formatApiError(e.response?.data?.detail)))} data-testid="keep-export-btn" className="border-status-passed text-status-passed hover:bg-status-passed/5 mr-2"><FileDown className="w-4 h-4 mr-1.5" /> Export</Button>
        {can() && <Button variant="outline" onClick={() => setCfgOpen(true)} data-testid="keep-settings-btn" className="border-taupe text-cocoa mr-2"><Settings2 className="w-4 h-4 mr-1.5" /> Pengaturan</Button>}
        {can("owner") && <Button variant="outline" onClick={repairStatus} data-testid="keep-status-repair-btn" className="border-amber-500 text-amber-700 hover:bg-amber-50 mr-2"><Wrench className="w-4 h-4 mr-1.5" /> Perbaiki Status</Button>}
        {can("owner") && <Button variant="outline" onClick={loadOrphans} data-testid="keep-orphans-btn" className="border-amber-600 text-amber-800 hover:bg-amber-50 mr-2"><Route className="w-4 h-4 mr-1.5" /> Keep Orphan</Button>}
        {can("owner", "warehouse_admin") && <Button variant="outline" onClick={loadRecon} data-testid="keep-reconcile-btn" className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 mr-2"><Boxes className="w-4 h-4 mr-1.5" /> Rekonsiliasi Keep</Button>}
        {write && <Button variant="outline" onClick={cleanup} data-testid="cleanup-keep-btn" className="border-status-reject text-status-reject hover:bg-status-reject/5 mr-2"><Trash2 className="w-4 h-4 mr-1.5" /> Bersihkan Keep</Button>}
        {write && <Button onClick={() => navigate("/sales/keep/create")} data-testid="add-keep-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory"><Plus className="w-4 h-4 mr-1.5" /> Buat Keep</Button>}
      </PageHeader>
      {cfgOpen && <KeepSettingsDialog onClose={() => setCfgOpen(false)} />}

      <Tabs defaultValue="list">
        <TabsList className="bg-white border border-taupe/20 mb-4">
          <TabsTrigger value="list" data-testid="tab-keep-list">Daftar Keep</TabsTrigger>
        </TabsList>

        <TabsContent value="list">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            {[["All Keeps", "all"], ["Online Keeps", "online"], ["Reseller Keeps", "reseller"]].map(([label, key]) => (
              <Panel key={key} className="p-4" data-testid={`keep-card-${key}`}>
                <div className="text-xs text-wmuted">{label}</div>
                <div className="text-2xl font-bold text-cocoa">{fmtIDR((sum[key] || {}).total || 0)}</div>
                <div className="flex items-center gap-1.5 text-xs text-status-passed mt-1.5">
                  <Boxes className="w-3.5 h-3.5" />
                  <span className="bg-status-passed/10 rounded px-1.5 py-0.5">{((sum[key] || {}).products || 0).toLocaleString("id-ID")} Product</span>
                </div>
                {(sum.period?.start || sum.period?.end) && (
                  <div className="flex items-center gap-1 text-[11px] text-wmuted mt-1.5">
                    <CalendarDays className="w-3 h-3" />
                    {fmtDate(sum.period.start)} — {fmtDate(sum.period.end)}
                  </div>
                )}
              </Panel>
            ))}
          </div>
          <div className="mb-4 flex items-center gap-2 flex-wrap">
            <Input value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="Cari kode keep / customer / produk / warna / ukuran / Order ID..." className="w-80 bg-white" data-testid="keep-search" />
            <Button variant="outline" onClick={doTrace} disabled={tracing} className="border-cocoa/30 text-cocoa" data-testid="keep-trace-btn"><Route className="w-4 h-4 mr-1.5" /> {tracing ? "Menelusuri…" : "Trace"}</Button>
            <Select value={statusF} onValueChange={chg(setStatusF)}>
              <SelectTrigger className="w-48 bg-white" data-testid="keep-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s === "all" ? "Semua Status" : s}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={source} onValueChange={chg(setSource)}>
              <SelectTrigger className="w-36 bg-white" data-testid="keep-source-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Semua Sumber</SelectItem><SelectItem value="wms">WMS</SelectItem><SelectItem value="legacy">Legacy</SelectItem></SelectContent>
            </Select>
            <Select value={bucket} onValueChange={chg(setBucket)}>
              <SelectTrigger className="w-40 bg-white" data-testid="keep-bucket-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Semua Bucket</SelectItem><SelectItem value="HOME">HOME</SelectItem><SelectItem value="STORE">STORE</SelectItem><SelectItem value="PRE_ORDER">PRE_ORDER</SelectItem></SelectContent>
            </Select>
          </div>

          {write && (
            <div className="mb-3 flex items-center gap-2 flex-wrap" data-testid="keep-bulk-toolbar">
              <Button size="sm" variant="outline" disabled={!selected.size || bulkBusy} onClick={bulkCancelSelected} className="border-status-reject text-status-reject hover:bg-status-reject/5" data-testid="bulk-cancel-selected-btn">
                <XCircle className="w-4 h-4 mr-1.5" />{bulkBusy ? "Memproses..." : `Batalkan Terpilih${selected.size ? ` (${selected.size})` : ""}`}
              </Button>
              <Button size="sm" variant="outline" disabled={bulkBusy || !data.total} onClick={bulkCancelAllFilter} className="border-status-reject text-status-reject hover:bg-status-reject/5" data-testid="bulk-cancel-all-btn">
                Batalkan Semua Hasil Filter
              </Button>
              {selected.size > 0 && <span className="text-xs text-wmuted">{selected.size} keep dipilih</span>}
            </div>
          )}

          <Panel>
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-10">{write && <Checkbox checked={allPageSelected} onCheckedChange={togglePage} disabled={!pageCancellableIds.length} data-testid="keep-select-all-page" />}</TableHead>
                  <TableHead className="w-12">No</TableHead><TableHead>No Keep</TableHead><TableHead>Bucket</TableHead><TableHead>Customer</TableHead>
                  <TableHead>Product</TableHead><TableHead>Color</TableHead><TableHead>Size</TableHead>
                  <TableHead className="text-center">Qty</TableHead><TableHead className="text-right">Price</TableHead><TableHead className="text-right">Total</TableHead>
                  <TableHead>Status</TableHead><TableHead>End Keep</TableHead><TableHead>Order ID</TableHead><TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {flatRows.map(({ k, it, first }, idx) => {
                  const qty = it ? ((it.qty_remaining != null ? it.qty_remaining : it.qty) || 0) : 0;
                  return (
                    <TableRow key={`${k.id}-${it ? it.id : "x"}`} data-testid={first ? `keep-row-${k.id}` : `keep-item-${k.id}-${idx}`}>
                      <TableCell>{first && write && CANCELLABLE.includes(k.status) && <Checkbox checked={selected.has(k.id)} onCheckedChange={() => toggleOne(k.id)} data-testid={`keep-select-${k.id}`} />}</TableCell>
                      <TableCell className="text-wmuted">{idx + 1}</TableCell>
                      <TableCell className={`font-mono text-sm font-semibold ${first ? "text-cocoa" : "text-cocoa/50"}`}>{k.number}</TableCell>
                      <TableCell>{first && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-cocoa/10 text-cocoa" data-testid={`keep-bucket-${k.id}`}>{k.sales_bucket || "HOME"}</span>}</TableCell>
                      <TableCell>{first ? <><div className="font-medium text-cocoa">{k.customer_name || "-"}</div>{k.group_name && <div className="text-[10px] text-taupe uppercase font-semibold">{k.group_name}</div>}</> : <span className="text-wmuted/50">↳</span>}</TableCell>
                      <TableCell className="text-sm text-cocoa">{it ? (it.product_name || "-") : "-"}</TableCell>
                      <TableCell className="text-sm">{it ? it.color : "-"}</TableCell>
                      <TableCell className="text-sm">{it ? it.size : "-"}</TableCell>
                      <TableCell className="text-center font-medium">{qty}</TableCell>
                      <TableCell className="text-right">{fmtIDR(it ? (it.price || 0) : 0)}</TableCell>
                      <TableCell className="text-right font-medium">{fmtIDR(it ? (it.price || 0) * qty : 0)}</TableCell>
                      <TableCell>{first && <StatusBadge status={k.status} />}</TableCell>
                      <TableCell className="text-sm">{first ? fmtDate(k.end_keep || k.created_at) : ""}</TableCell>
                      <TableCell className="text-sm text-wmuted">{first ? (k.order_id || "-") : ""}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {first && <>
                          <Button size="icon" variant="ghost" onClick={() => setDetail(k)} className="text-blue-600 h-8 w-8" data-testid={`keep-detail-${k.id}`}><Eye className="w-4 h-4" /></Button>
                          {["Active", "Partially Converted"].includes(k.status) && write && <Button size="icon" variant="ghost" onClick={() => setConvert(k)} className="text-status-passed h-8 w-8" title="Konversi ke Sales" data-testid={`keep-convert-${k.id}`}><ArrowRightLeft className="w-4 h-4" /></Button>}
                          {k.status === "Active" && write && <Button size="icon" variant="ghost" onClick={() => navigate(`/sales/keep/edit/${k.id}`)} className="text-cocoa h-8 w-8" title="Edit Keep" data-testid={`keep-edit-${k.id}`}><Pencil className="w-4 h-4" /></Button>}
                          {k.status === "Draft" && write && <Button size="sm" onClick={() => act(k.id, "activate")} className="bg-status-passed text-white h-7 px-2" data-testid={`keep-activate-${k.id}`}>Aktifkan</Button>}
                          {["Draft", "Active", "Partially Converted"].includes(k.status) && write && <Button size="icon" variant="ghost" onClick={() => act(k.id, "cancel", `Batalkan Keep ${k.number}? Stok akan dikembalikan ke available.`)} className="text-status-reject h-8 w-8" title="Batalkan" data-testid={`keep-cancel-${k.id}`}><Trash2 className="w-4 h-4" /></Button>}
                          {k.status !== "Converted to Sale" && write && <Button size="icon" variant="ghost" onClick={() => del(k)} className="text-status-reject h-8 w-8" title="Hapus permanen" data-testid={`keep-delete-${k.id}`}><XCircle className="w-4 h-4" /></Button>}
                        </>}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!flatRows.length && <TableRow><TableCell colSpan={15} className="text-center text-wmuted py-10"><Bookmark className="w-6 h-6 mx-auto mb-2 opacity-50" />Belum ada Keep Active.</TableCell></TableRow>}
              </TableBody>
            </Table>
            <div className="flex items-center justify-between px-4 py-3 border-t border-taupe/15 text-sm flex-wrap gap-3">
              <div className="flex items-center gap-2 text-wmuted">
                <span>Show</span>
                <Select value={String(limit)} onValueChange={(v) => { setLimit(parseInt(v)); setPage(1); }}>
                  <SelectTrigger className="w-20 h-8 bg-white" data-testid="keep-page-size"><SelectValue /></SelectTrigger>
                  <SelectContent>{[10, 25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                </Select>
                <span>entries · {((data.page - 1) * data.limit + (data.items.length ? 1 : 0)).toLocaleString("id-ID")}–{((data.page - 1) * data.limit + data.items.length).toLocaleString("id-ID")} dari {data.total.toLocaleString("id-ID")}</span>
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} className="border-taupe text-cocoa h-8" data-testid="keep-prev"><ChevronLeft className="w-4 h-4" /></Button>
                {pageWindow(data.page, data.pages || 1).map((p, i) => p === "..." ? (
                  <span key={`e${i}`} className="px-2 text-wmuted">…</span>
                ) : (
                  <Button key={p} size="sm" variant={p === data.page ? "default" : "outline"} onClick={() => setPage(p)} className={`h-8 w-8 p-0 ${p === data.page ? "bg-cocoa text-ivory" : "border-taupe text-cocoa"}`} data-testid={`keep-page-${p}`}>{p}</Button>
                ))}
                <Button size="sm" variant="outline" disabled={page >= (data.pages || 1)} onClick={() => setPage(page + 1)} className="border-taupe text-cocoa h-8" data-testid="keep-next"><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          </Panel>

          <ResumeKeep />
        </TabsContent>
      </Tabs>

      <ConvertDialog keep={convert} setKeep={setConvert} onDone={load} />
      <Dialog open={orphanOpen} onOpenChange={(o) => !o && setOrphanOpen(false)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="keep-orphans-dialog">
          <DialogHeader><DialogTitle>Keep Orphan — nyangkut tanpa Sales ({orphans.length})</DialogTitle></DialogHeader>
          <div className="text-sm text-wmuted mb-2">Keep yang qty-nya sudah "dikonversi" (sisa 0) tapi dokumen Sales gagal terbuat. <b>Sudah dikirim</b> → "Jadikan Sales". <b>Belum dikirim</b> (masih Perlu Proses di Desty) → "Kembalikan Aktif" (undo konversi & kuota dipulihkan).</div>
          {orphanBusy && <div className="text-sm text-cocoa py-2">Memproses…</div>}
          {!orphanBusy && !orphans.length && <div className="text-sm text-wmuted py-6 text-center">Tidak ada keep orphan. 🎉</div>}
          {!!orphans.length && (
            <Table>
              <TableHeader><TableRow><TableHead>No Keep</TableHead><TableHead>Order ID</TableHead><TableHead>Customer</TableHead><TableHead>Bucket</TableHead><TableHead className="text-center">Qty</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>{orphans.map((o) => (
                <TableRow key={o.id} data-testid={`orphan-row-${o.id}`}>
                  <TableCell className="font-medium">{o.number}</TableCell>
                  <TableCell className="font-mono text-xs">{o.order_id || "-"}</TableCell>
                  <TableCell>{o.customer_name || "-"}</TableCell>
                  <TableCell>{o.sales_bucket || "-"}</TableCell>
                  <TableCell className="text-center">{o.converted_qty}</TableCell>
                  <TableCell className="text-right space-x-1 whitespace-nowrap"><Button size="sm" variant="outline" disabled={orphanBusy} onClick={() => reconstructOrphans([o.id])} data-testid={`orphan-fix-${o.id}`}>Jadikan Sales</Button><Button size="sm" variant="ghost" disabled={orphanBusy} onClick={() => rollbackOrphans([o.id])} data-testid={`orphan-rollback-${o.id}`} className="text-amber-700">Kembalikan Aktif</Button></TableCell>
                </TableRow>
              ))}</TableBody>
            </Table>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOrphanOpen(false)}>Tutup</Button>
            {!!orphans.length && <Button variant="outline" disabled={orphanBusy} onClick={() => rollbackOrphans(null)} className="border-amber-600 text-amber-800 hover:bg-amber-50" data-testid="orphan-rollback-all-btn">Kembalikan Semua ke Aktif</Button>}
            {!!orphans.length && <Button disabled={orphanBusy} onClick={() => reconstructOrphans(null)} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="orphan-reconstruct-all-btn">Jadikan Sales Semua ({orphans.length})</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={reconOpen} onOpenChange={(o) => !o && setReconOpen(false)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="keep-reconcile-dialog">
          <DialogHeader><DialogTitle>Rekonsiliasi Keep — selisih bucket vs keep aktif</DialogTitle></DialogHeader>
          <div className="text-sm text-wmuted mb-2">Membandingkan stok fisik di bucket <b>keep</b> (yang bikin badge Keep di daftar produk) dengan total keep <b>aktif</b> per gudang. Selisih positif = stok "nyangkut" (reservasi keep yatim) yang bisa dikembalikan ke stok tersedia. Selisih negatif hanya ditampilkan untuk investigasi.</div>
          {reconBusy && <div className="text-sm text-cocoa py-2">Memproses…</div>}
          {!reconBusy && !recon.items.length && <div className="text-sm text-wmuted py-6 text-center">Semua sinkron. Tidak ada selisih. 🎉</div>}
          {!!recon.items.length && (
            <>
              <div className="flex gap-4 text-sm mb-2">
                <span className="text-emerald-700" data-testid="recon-orphan-total">Keep yatim: <b>{recon.orphan_total}</b> pcs</span>
                {recon.missing_total > 0 && <span className="text-status-reject" data-testid="recon-missing-total">Reservasi kurang: <b>{recon.missing_total}</b> pcs</span>}
                <span className="text-wmuted">{recon.total_rows} baris</span>
              </div>
              <Table>
                <TableHeader><TableRow><TableHead>Produk</TableHead><TableHead>Warna/Ukuran</TableHead><TableHead>Gudang</TableHead><TableHead className="text-center">Bucket Keep</TableHead><TableHead className="text-center">Keep Aktif</TableHead><TableHead className="text-center">Selisih</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>{recon.items.map((r) => (
                  <TableRow key={`${r.variant_id}-${r.warehouse_id}`} data-testid={`recon-row-${r.variant_id}-${r.warehouse_id}`} className={r.orphan > 0 ? "bg-emerald-50/50" : "bg-red-50/40"}>
                    <TableCell className="font-medium">{r.product_name}</TableCell>
                    <TableCell className="text-xs">{r.color} / {r.size}</TableCell>
                    <TableCell className="text-xs">{r.warehouse_name}</TableCell>
                    <TableCell className="text-center">{r.keep_bucket}</TableCell>
                    <TableCell className="text-center">{r.active_keep}</TableCell>
                    <TableCell className={`text-center font-semibold ${r.orphan > 0 ? "text-emerald-700" : "text-status-reject"}`}>{r.orphan > 0 ? `+${r.orphan}` : r.orphan}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">{r.orphan > 0 && <Button size="sm" variant="outline" disabled={reconBusy} onClick={() => applyRecon([{ variant_id: r.variant_id, warehouse_id: r.warehouse_id }])} data-testid={`recon-fix-${r.variant_id}-${r.warehouse_id}`}>Kembalikan</Button>}</TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReconOpen(false)}>Tutup</Button>
            {recon.orphan_total > 0 && <Button disabled={reconBusy} onClick={() => applyRecon(null)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="recon-apply-all-btn">Kembalikan Semua ({recon.orphan_total} pcs)</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <DetailDialog keep={detail} setKeep={setDetail} />
      <TraceDialog data={traceData} setData={setTraceData} />
    </div>
  );
}

function TraceDialog({ data, setData }) {
  const [busy, setBusy] = useState(false);
  if (!data) return null;
  const k = data.keep;
  const mismatch = k && k.derived_status && k.derived_status !== k.status;
  const canCashier = k && ["Active", "Partially Converted"].includes(k.status) && (k.order_id || data.order_id);
  const cashierNow = async () => {
    const oid = k.order_id || data.order_id;
    if (!window.confirm(`Proses kasir order ${oid} sekarang? Keep ${k.number} akan dijadikan Sales (abaikan batas Auto Kasir).`)) return;
    setBusy(true);
    try {
      const r = (await api.post("/desty/cashier-one", { order_sn: oid })).data;
      toast.success(r.message || "Berhasil diproses");
      const rt = (await api.get("/keep/trace", { params: { q: data.query } })).data;
      setData(rt);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!data} onOpenChange={(o) => !o && setData(null)}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="keep-trace-dialog">
        <DialogHeader><DialogTitle>Trace — {data.query} {data.order_id && data.order_id !== data.query ? `(Order ${data.order_id})` : ""}</DialogTitle></DialogHeader>
        <div className="space-y-5 text-sm">
          <section>
            <div className="font-semibold text-cocoa mb-1">Keep</div>
            {!k ? <div className="text-wmuted">Tidak ada Keep untuk kata kunci ini.</div> : (
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span><b>{k.number}</b></span>
                  <span>Bucket: {k.sales_bucket || "-"}</span>
                  <span>Customer: {k.customer_name || "-"}</span>
                  <span>Order ID: <span className="font-mono">{k.order_id || "-"}</span></span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span>Status tersimpan: <StatusBadge status={k.status} /></span>
                  {mismatch && <span className="text-amber-700">→ seharusnya <b>{k.derived_status}</b> (klik "Perbaiki Status" untuk merapikan)</span>}
                  {canCashier && <Button size="sm" onClick={cashierNow} disabled={busy} className="bg-cocoa hover:bg-cocoa-dark text-ivory h-8" data-testid="keep-trace-cashier-btn">{busy ? "Memproses…" : "Proses Kasir (Desty)"}</Button>}
                </div>
                <Table>
                  <TableHeader><TableRow><TableHead>Produk</TableHead><TableHead>Varian</TableHead><TableHead className="text-center">Qty</TableHead><TableHead className="text-center">Sisa</TableHead><TableHead className="text-center">Dikonversi</TableHead></TableRow></TableHeader>
                  <TableBody>{k.items.map((it, i) => (
                    <TableRow key={i}><TableCell>{it.product_name || "-"}</TableCell><TableCell>{it.color}/{it.size}</TableCell>
                      <TableCell className="text-center">{it.qty}</TableCell><TableCell className="text-center">{it.qty_remaining}</TableCell><TableCell className="text-center">{it.qty_converted}</TableCell></TableRow>
                  ))}</TableBody>
                </Table>
              </div>
            )}
          </section>
          <section>
            <div className="font-semibold text-cocoa mb-1">Sales terkait ({data.sales.length})</div>
            {!data.sales.length ? <div className="text-wmuted">Belum ada Sales.</div> : data.sales.map((s, i) => (
              <div key={i} className="border border-taupe/20 rounded p-2 mb-2">
                <div className="flex flex-wrap gap-x-4"><span><b>{s.number}</b></span><span>Status: {s.status}</span><span>{fmtDate(s.created_at)}</span><span>Total: {fmtIDR(s.grand_total || 0)}</span></div>
                <div className="text-xs text-wmuted mt-1">{s.items.map((it) => `${it.product_name} ${it.color}/${it.size} ×${it.qty}`).join(" · ")}</div>
              </div>
            ))}
          </section>
          <section>
            <div className="font-semibold text-cocoa mb-1">Pergerakan Stok ({data.movements.length})</div>
            {!data.movements.length ? <div className="text-wmuted">Tidak ada pergerakan.</div> : (
              <Table>
                <TableHeader><TableRow><TableHead>Waktu</TableHead><TableHead>Aktivitas</TableHead><TableHead>Bucket</TableHead><TableHead className="text-center">Qty</TableHead><TableHead>Ref</TableHead></TableRow></TableHeader>
                <TableBody>{data.movements.map((m, i) => (
                  <TableRow key={i}><TableCell className="text-xs">{fmtDate(m.datetime)}</TableCell><TableCell className="text-xs">{m.type}</TableCell>
                    <TableCell className="text-xs">{m.from_status || "-"} → {m.to_status || "-"}</TableCell><TableCell className="text-center">{m.qty_change}</TableCell>
                    <TableCell className="text-xs font-mono">{m.reference}</TableCell></TableRow>
                ))}</TableBody>
              </Table>
            )}
          </section>
          <section>
            <div className="font-semibold text-cocoa mb-1">Log Desty</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
              {[["Auto Keep", data.desty.autokeep], ["Auto Kasir", data.desty.autocashier], ["Auto Cancel", data.desty.autocancel]].map(([label, d], i) => (
                <div key={i} className="border border-taupe/20 rounded p-2">
                  <div className="font-medium text-cocoa">{label}</div>
                  {!d ? <div className="text-wmuted">-</div> : <div>Status: <b>{d.status}</b>{d.reason ? <div className="text-red-600 mt-0.5">{d.reason}</div> : null}</div>}
                </div>
              ))}
            </div>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const CHART_COLORS = ["#8B6F47", "#5A6B8C", "#9B7BB0", "#C77DA1", "#5E8B7E", "#B08968", "#D4A373", "#A3B18A", "#6D8B74", "#B5838D", "#7B9EA8", "#9C6644", "#E29578", "#83A598", "#C9ADA7", "#6F5E76"];

function ResumeKeep() {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + "01";
  const [start, setStart] = useState(monthStart);
  const [startTime, setStartTime] = useState("00:00");
  const [end, setEnd] = useState(today);
  const [endTime, setEndTime] = useState("23:59");
  const [group, setGroup] = useState("all");
  const [groups, setGroups] = useState([]);
  const [data, setData] = useState({ items: [], total: 0, total_qty: 0 });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  useEffect(() => { api.get("/customer-groups", { params: { active: "active" } }).then((r) => setGroups(r.data)).catch(() => {}); }, []);
  const load = () => api.get("/keep/resume", { params: { start, end, group } }).then((r) => { setData(r.data); setPage(1); }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [start, end, group]);

  const pageItems = data.items.slice((page - 1) * limit, page * limit);
  const pages = Math.max(Math.ceil(data.items.length / limit), 1);
  const chartData = data.items.slice(0, 16).map((r) => ({ name: `${r.name} ${r.color} ${r.size}`, value: r.items }));

  const exportCsv = () => {
    const header = "No,Name,Color,Size,Items\n";
    const body = data.items.map((r, i) => `${i + 1},"${r.name}","${r.color}","${r.size}",${r.items}`).join("\n");
    const blob = new Blob([header + body], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `resume-keep-${start}_${end}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6" data-testid="resume-keep-section">
      <Panel className="p-5">
        <div className="font-semibold text-cocoa mb-3">Keep Statistic</div>
        {chartData.length ? (
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <ResponsiveContainer width={220} height={220}>
              <PieChart>
                <Pie data={chartData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={90} paddingAngle={1}>
                  {chartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [`${v} pcs`, n]} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex-1 max-h-56 overflow-y-auto w-full space-y-1">
              {chartData.map((d, i) => (
                <div key={i} className="flex items-center gap-2 text-xs" data-testid={`keep-stat-legend-${i}`}>
                  <span className="w-3 h-3 rounded-sm shrink-0" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                  <span className="text-cocoa truncate">{d.name}</span>
                  <span className="ml-auto text-wmuted font-medium">{d.value}</span>
                </div>
              ))}
            </div>
          </div>
        ) : <div className="text-center text-wmuted py-16 text-sm">Tidak ada data pada rentang ini.</div>}
        <div className="text-xs text-wmuted mt-3">Penjualan Produk Per Kategori</div>
      </Panel>

      <Panel className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold text-cocoa">Resume Keep</div>
          <Button size="sm" onClick={exportCsv} className="bg-status-passed hover:bg-status-passed/90 text-white" data-testid="resume-export-btn"><FileDown className="w-4 h-4 mr-1.5" /> Export</Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
          <div><Label className="text-xs">Start Date</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="bg-white h-9" data-testid="resume-start-date" /></div>
          <div><Label className="text-xs">Start Time</Label><Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="bg-white h-9" data-testid="resume-start-time" /></div>
          <div><Label className="text-xs">End Date</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="bg-white h-9" data-testid="resume-end-date" /></div>
          <div><Label className="text-xs">End Time</Label><Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="bg-white h-9" data-testid="resume-end-time" /></div>
          <div><Label className="text-xs">Group</Label>
            <Select value={group} onValueChange={setGroup}>
              <SelectTrigger className="bg-white h-9" data-testid="resume-group-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Group</SelectItem>{groups.map((g) => <SelectItem key={g.id} value={g.name}>{g.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent"><TableHead className="w-12">No</TableHead><TableHead>Name</TableHead><TableHead>Color</TableHead><TableHead>Size</TableHead><TableHead className="text-right">Items</TableHead></TableRow></TableHeader>
          <TableBody>
            {pageItems.map((r, i) => (
              <TableRow key={`${r.name}-${r.color}-${r.size}`} data-testid={`resume-row-${(page - 1) * limit + i}`}>
                <TableCell className="text-wmuted">{(page - 1) * limit + i + 1}</TableCell>
                <TableCell className="text-cocoa">{r.name}</TableCell>
                <TableCell>{r.color}</TableCell>
                <TableCell>{r.size}</TableCell>
                <TableCell className="text-right font-medium">{r.items}</TableCell>
              </TableRow>
            ))}
            {!pageItems.length && <TableRow><TableCell colSpan={5} className="text-center text-wmuted py-8">Tidak ada data.</TableCell></TableRow>}
          </TableBody>
        </Table>
        <div className="flex items-center justify-between px-1 py-3 text-sm flex-wrap gap-3">
          <div className="flex items-center gap-2 text-wmuted">
            <span>Show</span>
            <Select value={String(limit)} onValueChange={(v) => { setLimit(parseInt(v)); setPage(1); }}>
              <SelectTrigger className="w-20 h-8 bg-white" data-testid="resume-page-size"><SelectValue /></SelectTrigger>
              <SelectContent>{[10, 25, 50].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
            </Select>
            <span>entries · {data.items.length ? (page - 1) * limit + 1 : 0}–{Math.min(page * limit, data.items.length)} dari {data.items.length}</span>
          </div>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} className="border-taupe text-cocoa h-8" data-testid="resume-prev"><ChevronLeft className="w-4 h-4" /></Button>
            {pageWindow(page, pages).map((p, i) => p === "..." ? <span key={`e${i}`} className="px-2 text-wmuted">…</span> : (
              <Button key={p} size="sm" variant={p === page ? "default" : "outline"} onClick={() => setPage(p)} className={`h-8 w-8 p-0 ${p === page ? "bg-cocoa text-ivory" : "border-taupe text-cocoa"}`} data-testid={`resume-page-${p}`}>{p}</Button>
            ))}
            <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)} className="border-taupe text-cocoa h-8" data-testid="resume-next"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      </Panel>
    </div>
  );
}

function PreparationTab() {
  const { can } = useAuth();
  const [data, setData] = useState({ items: [], total_lines: 0, total_qty: 0, prepared_lines: 0 });
  const canMark = can("warehouse_admin", "sales", "picker");
  const load = () => api.get("/keep/preparation").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const toggle = async (it, checked) => {
    try { await api.post("/keep/preparation/mark", { variant_id: it.variant_id, warehouse_id: it.warehouse_id, prepared: checked, total_qty: it.total_qty }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="keep-preparation">
      <div className="flex items-center gap-4 mb-4 text-sm text-cocoa flex-wrap">
        <span className="inline-flex items-center gap-1.5"><Truck className="w-4 h-4 text-cocoa" /> {data.total_lines} jenis barang</span>
        <span className="text-wmuted">·</span>
        <span>Total <b>{data.total_qty}</b> pcs perlu disiapkan</span>
        <span className="text-wmuted">·</span>
        <span className="text-status-passed"><b>{data.prepared_lines}/{data.total_lines}</b> sudah disiapkan</span>
        <Button size="sm" variant="outline" onClick={() => downloadFile("/keep/preparation/export", {}, "daftar-persiapan.xlsx")} className="border-taupe text-cocoa ml-auto" data-testid="prep-export-btn"><FileDown className="w-4 h-4 mr-1.5" />Export Excel</Button>
      </div>
      <Panel>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-16 text-center">Siap</TableHead>
              <TableHead>Gudang</TableHead><TableHead>Produk</TableHead><TableHead>Varian</TableHead>
              <TableHead className="text-center">Qty Disiapkan</TableHead><TableHead>Untuk (Keep / Customer)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map((it) => (
              <TableRow key={`${it.variant_id}-${it.warehouse_id}`} data-testid={`prep-row-${it.variant_id}`} className={it.prepared ? "bg-status-passed/5" : ""}>
                <TableCell className="text-center">
                  <Checkbox checked={it.prepared} disabled={!canMark} onCheckedChange={(v) => toggle(it, !!v)} data-testid={`prep-check-${it.variant_id}`} />
                </TableCell>
                <TableCell className="text-sm">{it.warehouse_name}</TableCell>
                <TableCell className={`font-semibold ${it.prepared ? "text-wmuted line-through" : "text-cocoa"}`}>{it.product_name}</TableCell>
                <TableCell className="text-sm">{it.color}/{it.size}</TableCell>
                <TableCell className="text-center"><span className="inline-flex items-center justify-center min-w-8 px-2 py-0.5 rounded-full bg-cocoa/10 text-cocoa font-bold">{it.total_qty}</span></TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1.5">
                    {it.keeps.map((k, i) => (
                      <span key={i} className="text-xs bg-ivory border border-taupe/20 px-2 py-0.5 rounded text-cocoa">
                        <span className="font-mono">{k.number}</span> · {k.customer_name || "-"} <b>×{k.qty}</b>
                      </span>
                    ))}
                  </div>
                  {it.prepared && it.prepared_by && <div className="text-xs text-status-passed mt-1">✓ Disiapkan oleh {it.prepared_by}</div>}
                </TableCell>
              </TableRow>
            ))}
            {!data.items.length && <TableRow><TableCell colSpan={6} className="text-center text-wmuted py-10"><PackageCheck className="w-6 h-6 mx-auto mb-2 opacity-50" />Tidak ada barang yang perlu disiapkan saat ini.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Panel>
    </div>
  );
}

function ConvertDialog({ keep, setKeep, onDone }) {
  const [qty, setQty] = useState({});
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    if (keep) { const q = {}; (keep.items || []).forEach((it) => { q[it.id] = it.qty_remaining; }); setQty(q); setSubmitting(false); }
  }, [keep]);
  if (!keep) return null;

  const remainingItems = (keep.items || []).filter((it) => it.qty_remaining > 0);

  const submit = async () => {
    if (submitting) return;
    const items = remainingItems
      .map((it) => ({ keep_item_id: it.id, qty: parseInt(qty[it.id]) || 0 }))
      .filter((x) => x.qty > 0);
    if (!items.length) return toast.error("Tentukan qty konversi minimal satu item");
    for (const it of remainingItems) {
      const q = parseInt(qty[it.id]) || 0;
      if (q > it.qty_remaining) return toast.error(`Qty konversi ${it.product_name} melebihi sisa (${it.qty_remaining})`);
    }
    const idempotency_key = `${keep.id}-${Date.now()}`;
    setSubmitting(true);
    try { await api.post(`/keep/${keep.id}/convert`, { items, idempotency_key }); toast.success("Keep dikonversi ke Sales (allocated)"); setKeep(null); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setSubmitting(false); }
  };

  return (
    <Dialog open={!!keep} onOpenChange={(v) => !v && setKeep(null)}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">Konversi Keep {keep.number} → Sales</DialogTitle></DialogHeader>
        <p className="text-xs text-wmuted -mt-2">KEEP → SALES_ALLOCATED. Konversi mengurangi keep & menambah alokasi sales (stok fisik belum keluar). Boleh parsial.</p>
        <div className="space-y-2 mt-2">
          {remainingItems.map((it) => (
            <div key={it.id} className="grid grid-cols-[1fr_auto] gap-3 items-center border border-taupe/15 rounded px-3 py-2">
              <div>
                <div className="text-sm font-medium text-cocoa">{it.product_name}</div>
                <div className="text-xs text-wmuted">{it.color}/{it.size} · sisa {it.qty_remaining} dari {it.qty}</div>
              </div>
              <Input type="number" className="w-24" value={qty[it.id] ?? 0} max={it.qty_remaining}
                onChange={(e) => setQty({ ...qty, [it.id]: e.target.value })} data-testid={`keep-convert-qty-${it.id}`} />
            </div>
          ))}
          {!remainingItems.length && <div className="text-sm text-wmuted">Tidak ada sisa untuk dikonversi.</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setKeep(null)} className="border-taupe text-cocoa">Batal</Button>
          <Button onClick={submit} disabled={submitting} data-testid="submit-convert-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory">{submitting ? "Memproses..." : "Konversi ke Sales"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailDialog({ keep, setKeep }) {
  if (!keep) return null;
  const items = keep.items || [];
  const total = items.reduce((s, it) => s + (it.price || 0) * (it.qty || 0), 0);
  const isOnline = (keep.group_name || "").toLowerCase() === "online";
  return (
    <Dialog open={!!keep} onOpenChange={(v) => !v && setKeep(null)}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-cocoa flex items-center justify-between pr-6">Detail Keep<Button size="sm" variant="outline" onClick={() => printKeep(keep)} className="border-taupe text-cocoa" data-testid="print-keep-btn"><Printer className="w-4 h-4 mr-1.5" />Cetak Struk</Button></DialogTitle></DialogHeader>

        <div className="rounded-lg border border-taupe/25 bg-ivory/50 p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 text-sm" data-testid="keep-detail-header">
          <div><div className="text-xs text-wmuted mb-0.5">No Keep:</div><div className="font-mono font-bold text-cocoa">{keep.number}</div></div>
          <div><div className="text-xs text-wmuted mb-0.5">Type:</div><div className="font-semibold text-cocoa">{keep.sales_bucket === "PRE_ORDER" ? "Pre-Order" : (isOnline ? "Online" : (keep.keep_type || "Regular"))}</div></div>
          <div><div className="text-xs text-wmuted mb-0.5">Customer Name:</div><div className="font-semibold text-cocoa">{keep.customer_name || "-"}</div></div>
          <div><div className="text-xs text-wmuted mb-0.5">End Keep:</div><div className="font-semibold text-cocoa">{fmtDate(keep.end_keep || keep.created_at)}</div></div>
          <div><div className="text-xs text-wmuted mb-0.5">Marketplace:</div><div className="font-semibold text-cocoa">{keep.marketplace || "-"}</div></div>
          <div><div className="text-xs text-wmuted mb-0.5">Order ID Marketplace:</div><div className="font-semibold text-cocoa break-all">{keep.order_id || "-"}</div></div>
        </div>

        <Table className="mt-2">
          <TableHeader><TableRow className="hover:bg-transparent">
            <TableHead className="w-12">No</TableHead><TableHead>Name</TableHead><TableHead>Color</TableHead><TableHead>Size</TableHead>
            <TableHead className="text-right">Price</TableHead><TableHead className="text-center">Qty</TableHead><TableHead className="text-right">Total Price</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((it, i) => (
              <TableRow key={it.id || i} data-testid={`keep-detail-item-${i}`}>
                <TableCell className="text-wmuted">{i + 1}</TableCell>
                <TableCell className="font-medium text-cocoa">{it.product_name || "-"}</TableCell>
                <TableCell>{it.color || "-"}</TableCell>
                <TableCell>{it.size || "-"}</TableCell>
                <TableCell className="text-right">{fmtIDR(it.price)}</TableCell>
                <TableCell className="text-center">{it.qty}</TableCell>
                <TableCell className="text-right font-medium">{fmtIDR((it.price || 0) * (it.qty || 0))}</TableCell>
              </TableRow>
            ))}
            {!items.length && <TableRow><TableCell colSpan={7} className="text-center text-wmuted py-6">Tidak ada item.</TableCell></TableRow>}
          </TableBody>
        </Table>
        <div className="flex items-center justify-end gap-6 mt-3 pr-2 text-base">
          <span className="font-semibold text-cocoa">Total Price:</span>
          <span className="font-bold text-cocoa" data-testid="keep-detail-total">{fmtIDR(total)}</span>
        </div>
        {keep.note && <div className="text-sm mt-3"><span className="text-wmuted">Catatan:</span> {keep.note}</div>}
        {keep.sales_refs?.length > 0 && <div className="text-sm mt-1"><span className="text-wmuted">Sales terkait:</span> {keep.sales_refs.map((s) => s.number).join(", ")}</div>}
      </DialogContent>
    </Dialog>
  );
}


function KeepSettingsDialog({ onClose }) {
  const [cfg, setCfg] = useState({ enabled: false, cancel_time: "21:00", group_ids: [] });
  const [groups, setGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/keep/settings").then((r) => {
      setCfg({ enabled: !!r.data.enabled, cancel_time: r.data.cancel_time || "21:00", group_ids: r.data.group_ids || [] });
      setGroups(r.data.groups || []);
    });
  }, []);
  const toggleG = (id) => setCfg((c) => ({ ...c, group_ids: c.group_ids.includes(id) ? c.group_ids.filter((x) => x !== id) : [...c.group_ids, id] }));
  const save = async () => {
    setSaving(true);
    try { await api.put("/keep/settings", cfg); toast.success("Pengaturan keep disimpan"); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="keep-settings-dialog">
        <DialogHeader><DialogTitle>Pengaturan Auto-Cancel Keep</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <label className="flex items-center gap-2 text-sm text-cocoa">
            <Checkbox checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: !!v })} data-testid="keep-cfg-enabled" />
            Aktifkan auto-cancel harian untuk Keep tipe <b>Regular</b>
          </label>
          <div>
            <Label>Jam auto-cancel (setiap hari, WIB)</Label>
            <Input type="time" value={cfg.cancel_time} onChange={(e) => setCfg({ ...cfg, cancel_time: e.target.value })} className="bg-white w-40" data-testid="keep-cfg-time" />
          </div>
          <div>
            <Label>Berlaku untuk Customer Group</Label>
            <p className="text-xs text-wmuted mb-1">Kosongkan = semua group. Keep Regular di group terpilih yang belum diproses akan otomatis dibatalkan (stok balik ke available).</p>
            <div className="space-y-1">
              {groups.map((g) => (
                <label key={g.id} className="flex items-center gap-2 text-sm text-cocoa">
                  <Checkbox checked={cfg.group_ids.includes(g.id)} onCheckedChange={() => toggleG(g.id)} data-testid={`keep-cfg-group-${g.id}`} /> {g.name}
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={saving} className="bg-cocoa text-ivory" data-testid="save-keep-settings">{saving ? "Menyimpan..." : "Simpan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
